import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';

/**
 * The four mobile tab identifiers. They drive both the hidden `IonTabBar`
 * (which Ionic uses to attribute routes to per-tab stacks) and the visible
 * floating `MobileBottomNav`.
 */
export const TAB_HOME = 'home' as const;
export const TAB_PROFILES = 'profiles' as const;
export const TAB_LEADS = 'leads' as const;
export const TAB_MORE = 'more' as const;

export type AppTab =
  | typeof TAB_HOME
  | typeof TAB_PROFILES
  | typeof TAB_LEADS
  | typeof TAB_MORE;

/**
 * Root path for each tab (`IonTabButton.href`). Sub-routes use the same prefix
 * (e.g. `/tabs/leads/:id`, `/tabs/more/products`).
 */
export const TAB_ROOTS: Record<AppTab, string> = {
  [TAB_HOME]: shellPaths.home,
  [TAB_PROFILES]: shellPaths.profiles,
  [TAB_LEADS]: shellPaths.leads,
  [TAB_MORE]: shellPaths.more,
};

/** Resolves the tab that owns a given in-shell path. */
export function resolveTabForPath(pathname: string): AppTab | undefined {
  const path = canonicalizeShellPathname(pathname);

  if (path === shellPaths.home || path.startsWith(`${shellPaths.home}/`)) {
    return TAB_HOME;
  }
  if (
    path === shellPaths.profiles ||
    path.startsWith(`${shellPaths.profiles}/`)
  ) {
    return TAB_PROFILES;
  }
  if (path === shellPaths.leads || path.startsWith(`${shellPaths.leads}/`)) {
    return TAB_LEADS;
  }
  if (path === shellPaths.more || path.startsWith(`${shellPaths.more}/`)) {
    return TAB_MORE;
  }
  return undefined;
}

/** Stack root `href` for a tab (More → hub when native bottom tab bar is shown). */
export function getTabRootPath(tab: AppTab): string {
  if (tab === TAB_MORE && shellUsesMobileTabBar()) {
    return shellPaths.moreHub;
  }
  return TAB_ROOTS[tab];
}

/** More tab stack root (hub on native, flat `/tabs/more` on browser). */
export function getMoreTabStackRoot(): string {
  return getTabRootPath(TAB_MORE);
}

/** True when pathname is the More tab stack root (not a pushed sub-page). */
export function isMoreTabStackRoot(pathname: string): boolean {
  const path = canonicalizeShellPathname(pathname);
  return path === getMoreTabStackRoot() || path === shellPaths.more;
}

/** True when pathname is a pushed page inside the More tab stack. */
export function isMoreTabSubPath(pathname: string): boolean {
  return (
    resolveTabForPath(pathname) === TAB_MORE && !isMoreTabStackRoot(pathname)
  );
}

/** True when pathname is the stack root for a tab (list/hub, not a pushed page). */
export function isTabStackRoot(pathname: string, tab: AppTab): boolean {
  if (tab === TAB_MORE) {
    return isMoreTabStackRoot(pathname);
  }
  return canonicalizeShellPathname(pathname) === TAB_ROOTS[tab];
}

/**
 * Re-tapping the already-active tab on its root page should scroll to top
 * (DEV-440). Nested pages keep existing pop-to-root / href behavior.
 */
export function shouldScrollActiveTabToTop(input: {
  clickedTab: AppTab;
  currentTab: AppTab | undefined;
  pathname: string;
}): boolean {
  return (
    input.currentTab === input.clickedTab &&
    isTabStackRoot(input.pathname, input.clickedTab)
  );
}
